import ParsingService from '../parsing-service';
import FormatConversionService from '../format-conversion-service';
import { prepareInputData, FORMULA_TYPES } from '@ply-io/node-input-parser';

import {
	FORMULAS_DELIMITER_PREFIX as PREFIX,
	FORMULAS_DELIMITER_SUFFIX as SUFFIX,
	FORMULAS_HTML_TAG,
	FORMULAS_KEYS,
	FORMULAS_TO_RICH_TEXT,
} from './formulas.const';
import {
	INSERT_DATA_DELIMITER_PREFIX,
	INSERT_DATA_DELIMITER_SUFFIX,
	TEXT_PARSER_DELIMITER_PREFIX,
	TEXT_PARSER_DELIMITER_SUFFIX,
} from '../DataChip/data-chip.const';
import { TEXT_BOX_FORMAT_TYPES } from '../rich-text-box.const';

import { excludedAttrsList } from '../HtmlAttributes/html-document-service';

// We replace the curley braces so they are not replaced when they are a parameter to a formula (and
// not a part of the content)
const PREFIX_CURRLY_BRACES_REPLACEMENTS = '(^_^|';
const SUFFIX_CURRLY_BRACES_REPLACEMENTS = '|^_^)';

/* Private */

function getSuffixAndPrefix(isTemplateMode) {
	const prefix = isTemplateMode ? TEXT_PARSER_DELIMITER_PREFIX : INSERT_DATA_DELIMITER_PREFIX;
	const suffix = isTemplateMode ? TEXT_PARSER_DELIMITER_SUFFIX : INSERT_DATA_DELIMITER_SUFFIX;
	return { prefix, suffix };
}

function replaceAllDelimitedDataToTipTapFormat(content, nodeId, options, config, isTemplateMode) {
	const getContentFunc = (delimitedContent) => ({
		content: encodeDataChipDelimiter(delimitedContent),
		mode: 1,
		options,
		nodeId,
		config,
		isTemplateMode,
	});

	const replaceFunc = (tokenizedContent) =>
		ParsingService.replaceAllDelimitedDataToTipTapFormat(PREFIX, SUFFIX, FORMULAS_HTML_TAG, tokenizedContent, getContentFunc);
	const result = ParsingService.tokenizeHtmlAttributesAndRun(excludedAttrsList, content, replaceFunc);
	return result;
}

function encodeDataChipDelimiter(content, isTemplateMode) {
	const { prefix, suffix } = getSuffixAndPrefix(isTemplateMode);
	return content.replaceAll(prefix, PREFIX_CURRLY_BRACES_REPLACEMENTS).replaceAll(suffix, SUFFIX_CURRLY_BRACES_REPLACEMENTS);
}
function decodeDataChipDelimiter(content, isTemplateMode) {
	const { prefix, suffix } = getSuffixAndPrefix(isTemplateMode);
	return content.replaceAll(PREFIX_CURRLY_BRACES_REPLACEMENTS, prefix).replaceAll(SUFFIX_CURRLY_BRACES_REPLACEMENTS, suffix);
}
/* Public */
function wrapFormulaArgs(...arg) {
	return [...arg].map((arg) => '"' + arg + '"').join(','); // "arg-1","arg-2","arg-3"
}
function wrapWithFormula(formulaName, ...arg) {
	return PREFIX + formulaName + '(' + wrapFormulaArgs(...arg) + ')' + SUFFIX;
}

function parseFunctionCall(input) {
	const functionNameMatch = input.match(/^(\w+)\(/);
	if (!functionNameMatch) return null;

	const functionName = functionNameMatch[1];
	const paramsSection = input.slice(functionName.length + 1, -1); // Remove the function name and the outer parentheses

	// Match quoted parameters, handling escaped quotes inside
	const paramsList = [...paramsSection.matchAll(/"((?:\\"|[^"])*)"/g)].map((match) => match[1].replace(/\\"/g, '"'));

	return {
		functionName,
		paramsList,
	};
}

function getFormulaFunctionData(formulaHtmlData) {
	formulaHtmlData = formulaHtmlData.replaceAll(PREFIX, '').replaceAll(SUFFIX, '');
	formulaHtmlData = formulaHtmlData.replaceAll(PREFIX_CURRLY_BRACES_REPLACEMENTS, '').replaceAll(SUFFIX_CURRLY_BRACES_REPLACEMENTS, '');
	return parseFunctionCall(formulaHtmlData);
}

function getPossibleFormulas(paramFormat, isMarkdown, isHtml) {
	const result = [];
	if (isHtml) {
		result.push(FORMULAS_KEYS.HTML_TO_TEXT);
	} else if (isMarkdown) {
		result.push(FORMULAS_KEYS.MARKDOWN_TO_TEXT);
	}

	switch (paramFormat) {
		case TEXT_BOX_FORMAT_TYPES.MARKDOWN:
			// if we are on a markdown we are really on html because this is how the client sends the data
			// and it will change the whole thing into markdown after
			if (isHtml) {
				result.push(FORMULAS_KEYS.HTML_TO_MARKDOWN_FAKE);
			} else if (isMarkdown) {
				result.push(FORMULAS_KEYS.MARKDOWN_TO_MARKDOWN_FAKE);
			}
			break;
		case TEXT_BOX_FORMAT_TYPES.HTML:
			if (isHtml) {
				result.push(FORMULAS_KEYS.HTML_TO_HTML);
			} else if (isMarkdown) {
				result.push(FORMULAS_KEYS.MARKDOWN_TO_HTML);
			}
			break;
		default:
			break;
	}

	return result;
}

function convertContentForFormulaChip(content, functionName, paramsList, options) {
	return FormatConversionService.convertToTiptapPresentation(content, functionName, paramsList, options);
}

function hasFormula(content) {
	return content.includes(PREFIX) && content.includes(SUFFIX);
}

function forEachFormulas(content, func) {
	return ParsingService.forEachDelimittedContent(PREFIX, SUFFIX, content, func);
}

function reformatParagraphAccordingToFormula(content) {
	const contentToReturn = ParsingService.mapAllLines(content, (row) => {
		let rowToReturn = row;
		forEachFormulas(row, (formula) => {
			if (hasFormula(row)) {
				const { functionName } = getFormulaFunctionData(formula);
				if (FORMULAS_TO_RICH_TEXT.includes(functionName)) {
					rowToReturn = PREFIX + formula + SUFFIX; // row should be only the "block level" formula
				}
			}
		});
		return rowToReturn;
	});
	return contentToReturn;
}

function toTiptapFormat(content, nodeId, options, config, showMultiSelectChips, paramKey, isTemplateMode) {
	return replaceAllDelimitedDataToTipTapFormat(content, nodeId, options, config, isTemplateMode);
}

function formatValueInFirstElementFormula(
	row,
	replaceMe,
	functionName,
	paramsList,
	previousStepsData = {},
	replaceInsertDataStringDict = {}
) {
	const [firstKey, ...restParams] = paramsList;
	const firstKeyWithInsertDataPrefixAndSuffix = firstKey.startsWith(INSERT_DATA_DELIMITER_PREFIX)
		? firstKey
		: INSERT_DATA_DELIMITER_PREFIX + firstKey + INSERT_DATA_DELIMITER_SUFFIX;
	const thisFormula = FormulasService.wrapWithFormula(functionName, firstKeyWithInsertDataPrefixAndSuffix, ...restParams);
	const input = { value: thisFormula };
	try {
		prepareInputData([], input, previousStepsData, replaceInsertDataStringDict);
	} catch (error) {
		console.log(error);
		return '';
	}
	return row.replace(replaceMe, input.value);
}

function runNoneUIFormulas(content, previousStepsData, replaceInsertDataStringDict) {
	let rowToReturn = content;
	forEachFormulas(content, (formula) => {
		const { functionName, paramsList } = getFormulaFunctionData(formula);
		if (functionName === FORMULA_TYPES.VALUE_IN_FIRST_ELEMENT) {
			const value = formatValueInFirstElementFormula(
				content,
				PREFIX + formula + SUFFIX,
				functionName,
				paramsList,
				previousStepsData,
				replaceInsertDataStringDict
			);
			rowToReturn = value;
		}
	});
	return rowToReturn;
}

function fromTiptapFormat(content, isTemplateMode) {
	const result = ParsingService.htmlTagsToCustomDelimiters(PREFIX, SUFFIX, FORMULAS_HTML_TAG, content);
	return decodeDataChipDelimiter(result, isTemplateMode);
}

const FormulasService = {
	wrapWithFormula,
	getFormulaFunctionData,
	getPossibleFormulas,
	convertContentForFormulaChip,
	hasFormula,
	reformatParagraphAccordingToFormula,
	fromTiptapFormat,
	toTiptapFormat,
	runNoneUIFormulas,
};

export default FormulasService;
